import React from 'react';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import classnames from 'classnames';
import moment from 'moment';
import { dateFormat } from 'lib/format';
import Select from 'common/Select';
import getContentFromListing from 'lib/format/getContentFromListing';
import ScheduleLink from './ScheduleLink';

const networkToImageName = { espn1: 'espn', espndeportes: 'espndeportes_blk' };

function checkListingAuthentication(listing, userAuth, preauthorizedResources) {
    if (!listing) {
        return false;
    }
    const adobeResource = listing.broadcasts[0].adobeResource;
    if (userAuth) {
        const authTypes = ['mvpd'];
        if (listing.broadcasts[0].ipAuth === true) {
            authTypes.push('isp');
        }
        const preauthStatus = preauthorizedResources.filter((ar) => ar.adobeId === adobeResource);
        if (preauthStatus[0]) {
            return preauthStatus[0].preauthorized;
        }
        return (authTypes.some(authType => userAuth.indexOf(authType) !== -1 || userAuth.indexOf(`unknown_${authType}`) !== -1));
    }
    return false;
}

class ScheduleCategorySection extends React.Component {
    constructor(props) {
        super(props);
        this.state = { selectedSubcategoryId: -1 };
        this.reset = false;
        this.onSubcategorySelect = this.onSubcategorySelect.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.selectedTab !== this.props.selectedTab) {
            this.setState({ selectedSubcategoryId: -1 });
            this.reset = true;
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        // Re-Render if:
        // (1) Number of listings changes in a section
        // (2) User authenticates
        // (3) Subcategory filter is used
        // (4) The first listing is different
        return (this.props.listings.length !== nextProps.listings.length) ||
            (this.props.userAuth.length !== nextProps.userAuth.length) ||
            (this.state.selectedSubcategoryId !== nextState.selectedSubcategoryId) ||
            (this.props.listings[0].id !== nextProps.listings[0].id);
    }

    componentDidUpdate() {
        if (window.espn_ui) {
            window.espn_ui.LoadBehavior();
        }
        this.reset = false;
    }

    onSubcategorySelect(option) {
        this.setState({ selectedSubcategoryId: option.value });
    }

    render() {
        const { category, isMobile, listings, preauthorizedResources, userAuth, userLocation, selectedTab } = this.props;
        const compareObjectsByName = (objA, objB) => {
            const aName = objA.name.trim().toLowerCase();
            const bName = objB.name.trim().toLowerCase();
            if (aName > bName) {
                return 1;
            } else if (aName < bName) {
                return -1;
            }
            return 0;
        };

        const subcategories = uniqBy(
            listings.map((listing) => listing.subcategories[0]), 'id'
        ).sort(compareObjectsByName);

        // moved the 'undefined' subcat to the top
        if (subcategories[subcategories.length - 1] === undefined) {
            subcategories.pop();
            subcategories.unshift(undefined);
        }

        const listingsGroupedBySubcategory = groupBy(listings,
            (listing) => listing.subcategories[0] && listing.subcategories[0].id);

        const hasDropdown = Object.keys(subcategories.filter((sc) => !!sc)).length > 1;

        const h2ClassName = classnames('table-caption', {
            'has-dropdown': hasDropdown
        });

        const selectOptions = [{ label: 'All Leagues', value: -1 }]
            .concat(
                subcategories
                    .filter((item) => !!item) // don't include the "undefined" subcat
                    .map((subcat) => ({ label: subcat.name, value: subcat.id }))
            );
        const select = hasDropdown && (
            <Select
                enabled={!this.reset}
                options={selectOptions}
                menuSizeClass="drop-left sm"
                onChange={this.onSubcategorySelect}
            />
        );
        const columns = selectedTab === 'replay' ? 5 : 4;
        const tableChildren = [];

        subcategories.forEach((subcategory) => {
            const subcatgoryNotFiltered =
                this.state.selectedSubcategoryId === -1 ||
                (!!subcategory && this.state.selectedSubcategoryId !== -1 && this.state.selectedSubcategoryId === subcategory.id);
            if (!!subcategory && subcatgoryNotFiltered) {
                tableChildren.push(
                    <thead key={`thead_${tableChildren.length}`}>
                        <tr>
                            <th colSpan={columns} ><span>{subcategory.name}</span></th>
                        </tr>
                    </thead>
                );
            } else {
                // insert a blank <thead> for listings without a subcat
                tableChildren.push(<thead key={`thead_${tableChildren.length}`} />);
            }

            const subcategoryListings =
                listingsGroupedBySubcategory[(subcategory && subcategory.id) || subcategory] || [];

            let filteredSubcategories = [];
            if (subcatgoryNotFiltered) {
                filteredSubcategories = subcategoryListings;
            }

            tableChildren.push((
                <tbody key={`tbody_${tableChildren.length}`}>
                    {filteredSubcategories.map((listing) => {
                        const userAuthenticatedForListing = (isMobile !== true) &&
                            checkListingAuthentication(listing, userAuth, preauthorizedResources);

                        const key = (!userAuthenticatedForListing) ? (
                            <span className="icon-font-before icon-key-solid-before" />
                        ) : null;

                        const networkAbbreviation = listing.broadcasts[0].abbreviation;
                        const networkLogoName = networkToImageName[networkAbbreviation] || networkAbbreviation;
                        const networkLogoImageSource =
                            `http://a.espncdn.com/prod/assets/watchespn_webplayer/channel_logo_${networkLogoName}_2x.png`;
                        const startTime = dateFormat.toISOString(listing.startTime);
                        const start = moment(startTime);
                        const time = start.format('h:mm A z');
                        const displayTime = (userLocation && userLocation.timeZone) ?
                            dateFormat.convertDateTime(startTime, { timeZone: userLocation.timeZone }) :
                            time;
                        const date = start.format('M/D');
                        const dateNode = selectedTab === 'replay' ? (<td className="schedule__airDate">{date}</td>) : null;

                        const placement = `schedule-${listing.type}`;
                        const listingContent = getContentFromListing(listing);
                        return (
                            <tr key={`tr_${listing.id}`}>
                                {dateNode}
                                <td className="schedule__time">{displayTime}</td>
                                <td className="schedule__competitors">
                                    <ScheduleLink
                                        content={listingContent}
                                        listing={listing}
                                        placement={placement}
                                    />
                                </td>
                                <td className="schedule__auth">{key}</td>
                                <td className="schedule__network">
                                    <img src={networkLogoImageSource} alt={networkAbbreviation} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            ));
        });

        return (
            <div key={category.id}>
                <h2 className={h2ClassName}>{category.name}</h2>
                {select}
                <div className="responsive-table-wrap">
                    <table
                        cellPadding="0"
                        cellSpacing="0"
                        className="schedule align-left"
                        data-text-contract="Contract table"
                        data-text-expand="Expand table"
                        data-fix-cols="0" data-behavior="responsive_table"
                    >
                        {tableChildren}
                    </table>
                </div>
            </div>
        );
    }
}

ScheduleCategorySection.propTypes = {
    category: React.PropTypes.object,
    isMobile: React.PropTypes.bool,
    listings: React.PropTypes.array,
    userAuth: React.PropTypes.array,
    userLocation: React.PropTypes.object,
    preauthorizedResources: React.PropTypes.array,
    selectedTab: React.PropTypes.string
};

module.exports = ScheduleCategorySection;



// WEBPACK FOOTER //
// ./app/schedule/ScheduleCategorySection.js